import { Button } from '@/atoms/Button';
import { Container } from '@/atoms/Container';
import { FlexColumn } from '@/atoms/FlexColumn';
import { FlexRow } from '@/atoms/FlexRow';
import {
  ButtonType,
  Card,
  CardSize,
  CardType,
  TagPosition,
} from '@/components/Card';
import { Gallery } from '@/components/Gallery';
import { LocationMap } from '@/components/LocationMap';
import { PaginationRoutes } from '@/components/PaginationRoutes';
import { DREAM_GAME_CENTER_CODE } from '@/constants';
import { useAdaptiveWidth } from '@/hooks/useAdaptiveWidth';
import { useEnhancedRouter } from '@/hooks/useEnhancedRouter';
import { Layout } from '@/layout';
import { fetchData } from '@/services/fetchData';
import {
  EntertainmentType,
  EventType,
  ObjectItemType,
  Palettes,
  PromotionType,
  SettingsType,
  Variants,
} from '@/types';
import { getCardRedirectUrl } from '@/utils/getCardRedirectUrl';
import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next';
import styles from './DreamGameCenter.module.scss';

const DreamGameCenter = ({
  menu,
  content,
  translations,
  common,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const itemData: ObjectItemType = content.item;
  const backendHTML = { __html: itemData.text };
  const { isMobile } = useAdaptiveWidth();
  const { push } = useEnhancedRouter();

  const handleEventsClick = () => {
    push('/events');
  };

  return (
    <Layout data={menu} meta={content.meta}>
      <main className={styles.page}>
        <PaginationRoutes
          routes={[
            {
              link: '/objects',
              text: common.linkObjects,
            },
            {
              link: `/objects/${itemData.code}`,
              text: itemData.name,
            },
          ]}
        />
        <Gallery
          galleryMob={itemData.galleryMob}
          gallery={itemData.gallery}
          title={itemData.name}
          alert={itemData.alert}
        />
        <div className="info" dangerouslySetInnerHTML={backendHTML} />
        {itemData.entertainments && itemData.entertainments.length > 0 && (
          <Container title={common.entertainments}>
            <FlexColumn>
              {itemData.entertainments.map((item: EntertainmentType) => (
                <Card
                  key={item.id}
                  picture={item.picture}
                  text={item.text}
                  code={item.code}
                  cardType={CardType.Filled}
                  size={CardSize.M}
                  tag={item.tag}
                  soon={item.soon}
                  closed={item.closed}
                  link={getCardRedirectUrl(item.type, item.code, item.link)}
                  tagPosition={TagPosition.Top}
                  buttonType={ButtonType.Arrow}
                  techClosed={item.techClosed}
                  pageMissing={item.pageMissing}
                  fullWidth={!!isMobile}
                />
              ))}
            </FlexColumn>
          </Container>
        )}
        {itemData.events && itemData.events.length > 0 && (
          <Container title="Афиша">
            <FlexRow>
              {itemData.events.slice(0, 4).map((item: EventType) => (
                <Card
                  key={item.id}
                  picture={item.picture}
                  name={item.name}
                  code={item.code}
                  cardType={CardType.Panel}
                  size={CardSize.SM}
                  dateFrom={item.dateFrom}
                  dateTo={item.dateTo}
                  link={`/events/${item.code}`}
                  tagPosition={TagPosition.Top}
                  info={item.info}
                  button={item.button}
                  noType
                  buttonLink={item.buttonLink}
                  twoRowTitle
                  withButton
                  pageMissing={item.pageMissing}
                  fullWidth={!!isMobile}
                />
              ))}
            </FlexRow>
            <Button
              text="Афиша"
              onClick={handleEventsClick}
              variant={Variants.Outline}
              palette={Palettes.Secondary}
              className={styles.more}
            />
          </Container>
        )}
        {itemData.location && (
          <LocationMap
            title={translations.mapTitle}
            img={itemData.location.picture}
            file={itemData.location.file}
            mobileImg={itemData.location.pictureMob}
            internal
          />
        )}
        {itemData.thingsToDo && itemData.thingsToDo.length > 0 && (
          <Container title={common.thingsToDoTitle}>
            <FlexColumn>
              {itemData.thingsToDo.map((item: PromotionType) => (
                <Card
                  key={item.id}
                  picture={item.picture}
                  link={item.link}
                  name={item.name}
                  size={CardSize.SM}
                  cardType={CardType.Panel}
                  text={undefined}
                  logo={item.logo}
                  noType
                  period={item.period}
                  description={item.text}
                  fullWidth
                />
              ))}
            </FlexColumn>
          </Container>
        )}
      </main>
    </Layout>
  );
};

export async function getServerSideProps({
  locale,
}: GetServerSidePropsContext) {
  const content = await fetchData({
    url: `/content/our-object/${DREAM_GAME_CENTER_CODE}/view`,
    locale,
  });

  const menu: SettingsType = await fetchData({
    url: `/settings/view`,
    locale,
  });

  const isError = !content || content.code !== 200;
  if (isError) return { notFound: true };

  return {
    props: {
      content,
      menu,
      translations: menu.translations['/objects/game-center'],
      common: menu.translations.common,
    },
  };
}

export default DreamGameCenter;
